import { useState } from 'react';
import { useFilamentos, useCriarFilamento, useAtualizarFilamento, useDeletarFilamento, useBulkDeletarFilamentos } from '@/hooks/useInsumos';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/Dialog';
import { Plus, Pencil, Trash2, CheckSquare, Square, Trash } from 'lucide-react';
import { formatCurrency, formatWeight, formatDate } from '@/lib/utils';
import type { Filamento } from '@/types';

export function Filamentos() {
  const { data: filamentos, isLoading } = useFilamentos();
  const criarFilamento = useCriarFilamento();
  const atualizarFilamento = useAtualizarFilamento();
  const deletarFilamento = useDeletarFilamento();
  const bulkDeletarFilamento = useBulkDeletarFilamentos();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [open, setOpen] = useState(false);
  const [selectedFilamento, setSelectedFilamento] = useState<Filamento | null>(null);
  const [formData, setFormData] = useState({
    material: 'PLA',
    cor: '',
    fabricante: '',
    pesoTotalComprado: '',
    custoPorKg: '',
    temperaturaNozzle: '200',
    temperaturaMesa: '60',
  });

  const handleEdit = (filamento: Filamento) => {
    setSelectedFilamento(filamento);
    setFormData({
      material: filamento.material,
      cor: filamento.cor,
      fabricante: filamento.fabricante,
      pesoTotalComprado: String(filamento.pesoTotalComprado),
      custoPorKg: String(filamento.custoPorKg),
      temperaturaNozzle: String(filamento.temperaturaNozzle),
      temperaturaMesa: String(filamento.temperaturaMesa),
    });
    setOpen(true);
  };

  const cleanNumber = (val: string | number) => {
    if (typeof val === 'string') {
      const cleaned = val.replace('.', '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return val;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      pesoTotalComprado: cleanNumber(formData.pesoTotalComprado),
      pesoAtualGramas: selectedFilamento ? selectedFilamento.pesoAtualGramas : cleanNumber(formData.pesoTotalComprado),
      custoPorKg: cleanNumber(formData.custoPorKg),
      temperaturaNozzle: cleanNumber(formData.temperaturaNozzle),
      temperaturaMesa: cleanNumber(formData.temperaturaMesa),
      dataCompra: selectedFilamento ? selectedFilamento.dataCompra : new Date().toISOString(),
    };

    if (selectedFilamento) {
      await atualizarFilamento.mutateAsync({ id: selectedFilamento.id, data: payload });
    } else {
      await criarFilamento.mutateAsync(payload);
    }
    
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedFilamento(null);
    setFormData({
      material: 'PLA',
      cor: '',
      fabricante: '',
      pesoTotalComprado: '',
      custoPorKg: '',
      temperaturaNozzle: '200',
      temperaturaMesa: '60',
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este filamento?')) {
      await deletarFilamento.mutateAsync(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === (filamentos?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filamentos?.map((p: any) => p.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir os ${selectedIds.length} filamentos selecionados?`)) {
      bulkDeletarFilamento.mutate(selectedIds, {
        onSuccess: (response: any) => {
          setSelectedIds([]);
          alert(response?.data?.message || 'Exclusão em massa concluída!');
        },
        onError: () => {
          alert('Erro ao realizar exclusão em massa');
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Filamentos</h1>
          <p className="text-gray-500">Gerencie seu estoque de filamentos</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50 transition-colors"
              onClick={handleBulkDelete}
              disabled={bulkDeletarFilamento.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button variant="nuvem" leftIcon={<Plus className="h-4 w-4" />}>
                Novo Filamento
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>{selectedFilamento ? 'Editar Filamento' : 'Cadastrar Filamento'}</DialogTitle>
              <DialogDescription>
                Informe as características do rolo de filamento.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Material"
                  value={formData.material}
                  onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                  required
                />
                <Input
                  label="Cor"
                  value={formData.cor}
                  onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                  required
                />
              </div>
              <Input
                label="Fabricante"
                value={formData.fabricante}
                onChange={(e) => setFormData({ ...formData, fabricante: e.target.value })}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Peso (gramas)"
                  type="number"
                  value={formData.pesoTotalComprado}
                  onChange={(e) => setFormData({ ...formData, pesoTotalComprado: e.target.value })}
                  required
                />
                <Input
                  label="Custo por KG"
                  type="number"
                  step="0.01"
                  value={formData.custoPorKg}
                  onChange={(e) => setFormData({ ...formData, custoPorKg: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" variant="nuvem" className="w-full" isLoading={criarFilamento.isPending || atualizarFilamento.isPending}>
                {selectedFilamento ? 'Atualizar' : 'Salvar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-4 w-10">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                        {selectedIds.length === (filamentos?.length || 0) && selectedIds.length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Material</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Cor</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Fabricante</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Estoque</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Custo/kg</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Data Compra</th>
                    <th className="py-4 px-6 text-right text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filamentos?.map((filamento: Filamento) => (
                    <tr key={filamento.id} className={`border-b border-gray-200/50 hover:bg-gray-50 transition-colors ${selectedIds.includes(filamento.id) ? 'bg-orange-50/30' : ''}`}>
                      <td className="py-4 px-4">
                        <button onClick={() => toggleSelect(filamento.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                          {selectedIds.includes(filamento.id) ? (
                            <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900">{filamento.material}</td>
                      <td className="py-4 px-6 text-gray-500">{filamento.cor}</td>
                      <td className="py-4 px-6 text-gray-500">{filamento.fabricante}</td>
                      <td className="py-4 px-6">
                        <span className={filamento.pesoAtualGramas < 500 ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {formatWeight(filamento.pesoAtualGramas)}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          / {formatWeight(filamento.pesoTotalComprado)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-gray-900">{formatCurrency(filamento.custoPorKg)}</td>
                      <td className="py-4 px-6 text-gray-500 text-sm">
                        {formatDate(filamento.dataCompra)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(filamento)}>
                            <Pencil className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(filamento.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
